import React from 'react';
import PropTypes from 'prop-types';
import * as ReactRedux from 'react-redux';
import { bindActionCreators } from 'redux';
import { AddRecipientsConfirmationModal, PublishTemplateConfirmationModal } from 'SelfServeModals';
import * as SelfServePostBuilderSidePanelActions from 'SelfServePostBuilderSidePanel/SelfServePostBuilderSidePanelActions';
import { setIsRecipientPreviewModalVisible, setPopupCreationError } from 'SelfServeBuilder/SelfServeBuilderActions';
import ModalTypeEnum from 'Modals/ModalConstants';
import { SelfServeBuilderResponsesNotifications } from 'SelfServeBuilderResponses/SelfServeBuilderResponsesConstants';
import RecipientManagerHeader from './RecipientManagerHeader';
import RecipientManagerFooter from './RecipientManagerFooter';
import RecipientManagerBody from './RecipientManagerBody';
import * as RecipientManagerActions from './RecipientManagerActions';


const PUBLIC_LINK_HASH = 'public-link';
const ADD_RECIPIENTS_HASH = 'add-recipients';

class RecipientManager extends React.Component {
  constructor(props) {
    super(props);

    this.addNewEmailField = this.addNewEmailField.bind(this);
    this.closePublishModal = this.closePublishModal.bind(this);
    this.closeRecipientModal = this.closeRecipientModal.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleMessageChange = this.handleMessageChange.bind(this);
    this.onClickSend = this.onClickSend.bind(this);
    this.onShareCustomTemplate = this.onShareCustomTemplate.bind(this);
    this.openPublishModal = this.openPublishModal.bind(this);
    this.openRecipientModal = this.openRecipientModal.bind(this);
    this.removeEmailField = this.removeEmailField.bind(this);

    this.showRecipientPreview = this.showRecipientPreview.bind(this);
    this.redirectToResponses = this.redirectToResponses.bind(this);

    // hasEmailInputBlurred stored in state at this level
    // to preserve this data when switching between Add Recipients
    // and Share Link tabs
    this.state = {
      emails: [''],
      hasEmailInputBlurred: [false],
      message: 'Hello, please complete this document at your earliest convenience. Thank you.',
      showPublishConfirmationModal: false,
      showRecipientsConfirmationModal: false,
    };
  }

  componentDidMount() {
    const hash = window.location.hash.substring(1);
    if (hash === PUBLIC_LINK_HASH) {
      this.props.showPublicLinkTab();
    } else if (hash === ADD_RECIPIENTS_HASH) {
      this.props.showAddRecipientsTab();
    }
    if (this.props.onMount) {
      this.props.onMount();
    }
  }

  onClickSend() {
    window._track('Send | Add Recipients', 'Template Builder');
    if (!this.props.publishedAt) {
      this.openRecipientModal();
    } else {
      this.onShareCustomTemplate();
    }
  }

  onShareCustomTemplate() {
    if (this.state.showRecipientsConfirmationModal) {
      window._track('Send Now | Add Recipients', 'Template Builder');
    }
    this.props.shareCustomTemplate(
      parseInt(window.COOKIE.get('userid'), 10),
      window.COOKIE.get('userToken'),
      this.props.documentType,
      this.state.emails,
      this.state.message,
      this.closeRecipientModal,
      this.props.isOverlayVisible ? this.redirectToResponses : null,
    );

    this.setState({
      emails: [''],
      hasEmailInputBlurred: [false],
    });
  }

  redirectToResponses() {
    const { documentType } = this.props;
    const notification = SelfServeBuilderResponsesNotifications.success;
    window.location.assign(`/self-serve-responses?documentType=${documentType}&notification=${notification}`);
  }

  handleEmailChange(emailNumber, event) {
    const newEmailList = this.state.emails;
    newEmailList[emailNumber] = event.target.value;
    this.setState({
      emails: newEmailList,
    });
  }

  handleMessageChange(event) {
    this.setState({
      message: event.target.value,
    });
  }

  handleBlur(emailIdx) {
    const newHasEmailInputBlurredList = this.state.hasEmailInputBlurred;
    newHasEmailInputBlurredList[emailIdx] = true;
    this.setState({
      hasEmailInputBlurred: newHasEmailInputBlurredList,
    });
  }

  addNewEmailField() {
    const newEmailList = this.state.emails;
    const newHasEmailInputBlurredList = this.state.hasEmailInputBlurred;
    newEmailList.push('');
    newHasEmailInputBlurredList.push(false);
    this.setState({
      emails: newEmailList,
      hasEmailInputBlurred: newHasEmailInputBlurredList,
    });
    window._track('Add New Recipient | Add Recipients', 'Template Builder');
  }

  removeEmailField(emailIdx) {
    const newEmailList = this.state.emails;
    const newHasEmailInputBlurredList = this.state.hasEmailInputBlurred;
    newEmailList.splice(emailIdx, 1);
    newHasEmailInputBlurredList.splice(emailIdx, 1);
    this.setState({
      emails: newEmailList,
      hasEmailInputBlurred: newHasEmailInputBlurredList,
    });
  }

  openRecipientModal() {
    this.setState(state => ({
      showRecipientsConfirmationModal: true,
      showPublishConfirmationModal: state.showPublishConfirmationModal,
    }));
  }

  closeRecipientModal() {
    this.setState(state => ({
      showRecipientsConfirmationModal: false,
      showPublishConfirmationModal: state.showPublishConfirmationModal,
    }));
  }

  openPublishModal() {
    this.setState(state => ({
      showPublishConfirmationModal: true,
      showRecipientsConfirmationModal: state.showRecipientsConfirmationModal,
    }));
  }

  closePublishModal() {
    this.setState(state => ({
      showPublishConfirmationModal: false,
      showRecipientsConfirmationModal: state.showRecipientsConfirmationModal,
    }));
  }

  showRecipientPreview() {
    this.props.setIsRecipientPreviewModalVisible(true);
  }

  render() {
    let activeModal;
    if (this.state.showRecipientsConfirmationModal) {
      activeModal = ModalTypeEnum.SELF_SERVE_ADD_RECIPIENTS_CONFIRM;
    } else if (this.state.showPublishConfirmationModal) {
      activeModal = ModalTypeEnum.SELF_SERVE_PUBLISH_TEMPLATE_CONFIRM;
    } else {
      activeModal = '';
    }

    return (
      <React.Fragment>
        <RecipientManagerHeader activeTab={this.props.activeTab} />
        <RecipientManagerBody
          activeTab={this.props.activeTab}
          addNewEmailField={this.addNewEmailField}
          clickCancel={this.props.closePanel}
          clickPublish={this.openPublishModal}
          clickSend={this.onClickSend}
          documentType={this.props.documentType}
          emails={this.state.emails}
          handleBlur={this.handleBlur}
          handleEmailChange={this.handleEmailChange}
          handleMessageChange={this.handleMessageChange}
          hasEmailInputBlurred={this.state.hasEmailInputBlurred}
          isEditingPopup={this.props.isEditingPopup}
          isPreviewButtonEnabled={this.props.isPreviewButtonEnabled}
          isPublic={this.props.isPublic}
          isTemplateEmpty={this.props.isTemplateEmpty}
          message={this.state.message}
          openPreview={this.showRecipientPreview}
          publishedAt={this.props.publishedAt}
          removeEmailField={this.removeEmailField}
          setPopupCreationError={this.props.setPopupCreationError}
        />
        {!this.props.publishedAt &&
          <RecipientManagerFooter clickBackToOrganizeSections={this.props.displayOrganizer} />
        }
        <AddRecipientsConfirmationModal
          activeModal={activeModal}
          close={this.closeRecipientModal}
          closePanel={this.props.closePanel}
          isOpen={this.state.showRecipientsConfirmationModal}
          send={this.onShareCustomTemplate}
        />
        <PublishTemplateConfirmationModal
          activeModal={activeModal}
          close={this.closePublishModal}
          isOpen={this.state.showPublishConfirmationModal}
          publish={this.props.publicLinkUpdateState}
          documentType={this.props.documentType}
        />
      </React.Fragment>
    );
  }
}

RecipientManager.defaultProps = {
  closePanel: () => {},
  confirmOnSend: true,
  isOverlayVisible: false,
  isPreviewButtonEnabled: true,
  isTemplateEmpty: false,
  onMount: () => null,
  publishedAt: null,
};

RecipientManager.propTypes = {
  activeTab: PropTypes.string.isRequired,
  closePanel: PropTypes.func.isRequired,
  displayOrganizer: PropTypes.func.isRequired,
  documentType: PropTypes.string.isRequired,
  isEditingPopup: PropTypes.bool.isRequired,
  isOverlayVisible: PropTypes.bool,
  isPreviewButtonEnabled: PropTypes.bool,
  isPublic: PropTypes.bool.isRequired,
  isTemplateEmpty: PropTypes.bool,
  onMount: PropTypes.func,
  publicLinkUpdateState: PropTypes.func.isRequired,
  publishedAt: PropTypes.string,
  shareCustomTemplate: PropTypes.func.isRequired,
  showAddRecipientsTab: PropTypes.func.isRequired,
  showPublicLinkTab: PropTypes.func.isRequired,
  setIsRecipientPreviewModalVisible: PropTypes.func.isRequired,
  setPopupCreationError: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    ...SelfServePostBuilderSidePanelActions,
    ...RecipientManagerActions,
    shareCustomTemplate: RecipientManagerActions.shareCustomTemplate,
    setIsRecipientPreviewModalVisible,
    setPopupCreationError,
  }, dispatch)
);

const mapStateToProps = ({ SelfServePostBuilderSidePanelHandler, popupHandler }) => ({
  activeTab: SelfServePostBuilderSidePanelHandler.RecipientManagerReducer.activeTab,
  isEditingPopup: popupHandler.isEditingPopup,
});

export default ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecipientManager);



// WEBPACK FOOTER //
// ./src-react/SelfServePostBuilderSidePanel/RecipientManager/index.jsx